/**
 * PaginaRecuperarSenha - Página para solicitar recuperação de senha
 * 
 * CONTEXTO DE NEGÓCIO:
 * Permite que usuários que esqueceram sua senha solicitem um link
 * de recuperação que será enviado por email.
 * 
 * FLUXO:
 * 1. Usuário insere seu email
 * 2. Sistema envia email com link de recuperação
 * 3. Usuário é redirecionado para mensagem de confirmação
 */

import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Mail, ArrowLeft, Loader2, CheckCircle } from 'lucide-react';
import { clienteApi } from '../servicos/clienteApi';

export function PaginaRecuperarSenha() {
  const [email, setEmail] = useState('');
  const [estaCarregando, setEstaCarregando] = useState(false);
  const [enviado, setEnviado] = useState(false);
  const [erro, setErro] = useState<string | null>(null);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setErro(null);
    setEstaCarregando(true);

    try {
      await clienteApi.post('/api/core/usuarios/recuperar-senha', { email });
      setEnviado(true);
    } catch (err) {
      // Mesmo em caso de erro, mostramos mensagem de sucesso por segurança
      // (não revelar se email existe ou não)
      setEnviado(true);
    } finally {
      setEstaCarregando(false);
    }
  };

  if (enviado) {
    return (
      <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-4">
        <div className="w-full rounded-lg bg-white p-8 shadow-lg">
          <div className="mb-6 text-center">
            <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-green-100">
              <CheckCircle className="h-8 w-8 text-green-600" />
            </div>
            <h1 className="text-2xl font-bold text-gray-900">Email Enviado!</h1>
            <p className="mt-2 text-gray-600">
              Se o email <strong>{email}</strong> estiver cadastrado, você receberá 
              instruções para recuperar sua senha.
            </p>
          </div>

          <div className="space-y-3">
            <p className="text-center text-sm text-gray-500">
              Não recebeu o email? Verifique sua pasta de spam ou aguarde alguns minutos.
            </p>
            
            <div className="flex flex-col gap-2">
              <button
                onClick={() => {
                  setEnviado(false);
                  setEmail('');
                }}
                className="w-full rounded-md border border-gray-300 px-4 py-2 text-gray-700 hover:bg-gray-50"
              >
                Tentar outro email
              </button>
              
              <Link
                to="/login"
                className="w-full rounded-md bg-primary-600 px-4 py-2 text-center text-white hover:bg-primary-700"
              >
                Voltar para o Login
              </Link>
            </div>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="mx-auto flex min-h-[70vh] max-w-md flex-col items-center justify-center px-4">
      <div className="w-full rounded-lg bg-white p-8 shadow-lg">
        <div className="mb-6 text-center">
          <div className="mx-auto mb-4 flex h-16 w-16 items-center justify-center rounded-full bg-blue-100">
            <Mail className="h-8 w-8 text-blue-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900">Recuperar Senha</h1>
          <p className="mt-2 text-gray-600">
            Digite seu email e enviaremos instruções para criar uma nova senha.
          </p>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          {erro && (
            <div className="rounded-md bg-red-50 p-3 text-sm text-red-600">
              {erro}
            </div>
          )}

          <div>
            <label htmlFor="email" className="mb-1 block text-sm font-medium text-gray-700">
              Email
            </label>
            <div className="relative">
              <span className="absolute inset-y-0 left-3 flex items-center text-gray-400">
                <Mail className="h-5 w-5" />
              </span>
              <input
                type="email"
                id="email"
                name="email"
                value={email}
                onChange={(e: React.ChangeEvent<HTMLInputElement>) => setEmail(e.target.value)}
                required
                placeholder="seu@email.com"
                className="block w-full rounded-md border py-2 pl-10 pr-3 focus:outline-none focus:ring-2 focus:ring-primary-600"
              />
            </div>
          </div>

          <button
            type="submit"
            disabled={estaCarregando || !email}
            className="inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary-600 px-4 py-2 font-medium text-white hover:bg-primary-700 disabled:opacity-60"
          >
            {estaCarregando ? (
              <>
                <Loader2 className="h-4 w-4 animate-spin" />
                Enviando...
              </>
            ) : (
              'Enviar link de recuperação'
            )}
          </button>
        </form>

        <div className="mt-6 text-center">
          <Link
            to="/login"
            className="inline-flex items-center gap-1 text-sm text-primary-600 hover:text-primary-700"
          >
            <ArrowLeft className="h-4 w-4" />
            Voltar para o Login
          </Link>
        </div>
      </div>
    </div>
  );
}

export default PaginaRecuperarSenha;
